import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { coverPan, homeFor, homeLight, HOME_ART_ASPECT } from '../../utils/portedHomes';

// 🚪 The painted front door (PORTED_HOME_HANDOFF_2026_09_25 §9, W3; mockup v9
// "4 · The door"). The owner comes back after a real gap (6 h+) and finds his
// home's OUTSIDE on the stage, at the owner's hour: knock → the brass knocker
// rings, the door glows, and the camera zooms through the door (1.25 s) into
// the painted home underneath; "just slip in" skips the knock. The knock is
// the REAL knock poke (the page sends it: his next turn hears it and answers
// in his own words); nothing HERE speaks for him. The mockup's "there you
// are." is ruled IN (ruling 31, one of two fixed lines in his hand): PortedHome
// shows it by his medallion after a knock's walk in, never after a slip in.
// Phones too (the old 120 px CSS door was desktop only).
//
// Rendered INSIDE the stage over the home, so the zoom lands on the room; the
// buttons live in the caption row (PortedStage's door bar), never on the art.
// Reduced motion: no zoom, a short fade. The outside is one dusk painting per
// home; the owner's hour tints it (night darkens it and the windows glow,
// midday lifts it). Class names avoid the substrings generated themes target.
const CSS = `
.pdr, .pdr * { box-sizing: border-box; }
.pdr { position: absolute; inset: 0; z-index: 9; overflow: hidden; background: #1b1320; transition: opacity .8s ease .55s; }
.pdr-box { position: absolute; left: 0; top: 0; transition: transform 1.25s cubic-bezier(.6,.05,.3,1); }
.pdr-paint { position: absolute; inset: 0; width: 100%; height: 100%; display: block; user-select: none; -webkit-user-drag: none; transition: filter 1.1s ease; }
.pdr-glow { position: absolute; transform: translate(-50%,-50%); border-radius: 50%; background: radial-gradient(closest-side, rgba(255,205,125,.75), rgba(255,190,110,0)); mix-blend-mode: screen; opacity: 0; transition: opacity .5s; pointer-events: none; }
.pdr-lit { position: absolute; transform: translate(-50%,-50%); width: 14%; height: 26%; border-radius: 50%; background: radial-gradient(closest-side, rgba(255,210,140,.55), rgba(255,190,110,0)); mix-blend-mode: screen; pointer-events: none; transition: opacity 1.1s; }
.pdr-knocker { position: absolute; transform: translate(-50%,-50%); width: 2.4%; aspect-ratio: 1; border-radius: 50%; pointer-events: none; }
.pdr-knock .pdr-glow { opacity: 1; }
.pdr-knock .pdr-knocker { animation: pdrKnock .95s ease-out; }
@keyframes pdrKnock { 0% { box-shadow: 0 0 0 0 rgba(255,214,150,.95); } 30% { box-shadow: 0 0 0 12px rgba(255,214,150,0); } 31% { box-shadow: 0 0 0 0 rgba(255,214,150,.95); } 62% { box-shadow: 0 0 0 12px rgba(255,214,150,0); } 63% { box-shadow: 0 0 0 0 rgba(255,214,150,.95); } 100% { box-shadow: 0 0 0 14px rgba(255,214,150,0); } }
.pdr-enter { opacity: 0; pointer-events: none; }
@media (prefers-reduced-motion: reduce) {
  .pdr { transition: opacity .4s ease; }
  .pdr-box { transition: none !important; }
  .pdr-knock .pdr-knocker { animation: none; box-shadow: 0 0 0 4px rgba(255,214,150,.8); }
}
`;

const KNOCK_MS = 1000;   // the knocker's three raps, then through the door
const ENTER_MS = 1400;   // the zoom (1.25 s) + the fade's tail

export default function PortedDoor({
  homeKey,
  minutes = 1140,       // the owner's clock
  stormy = false,       // W4: a storm or rain today (the outside darkens)
  phase = 'closed',     // 'closed' | 'knock' | 'enter' (PortedStage drives it)
  still = false,        // prefers-reduced-motion
  onEntered = null,     // the zoom finished: the home is the stage again
  residentName = null,
}) {
  const home = homeFor(homeKey);
  const o = home && home.outside;
  const boxRef = useRef(null);
  const [size, setSize] = useState({ W: 0, H: 0 });
  const missed = useRef(false);
  // the page may hand a new callback every render: the zoom's timer never restarts for it
  const enteredRef = useRef(onEntered);
  enteredRef.current = onEntered;
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const read = () => { const r = el.getBoundingClientRect(); setSize((s) => (s.W === r.width && s.H === r.height ? s : { W: r.width, H: r.height })); };
    read();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(read); ro.observe(el); }
    return () => { if (ro) ro.disconnect(); };
  }, []);
  useEffect(() => {
    if (phase !== 'enter') return undefined;
    const t = setTimeout(() => { if (enteredRef.current) enteredRef.current(); }, still ? 450 : ENTER_MS);
    return () => clearTimeout(t);
  }, [phase, still]);
  if (!o) return null;

  const L = homeLight(minutes / 60);
  // the painting is dusk: night darkens it (the windows keep glowing), midday lifts it
  const filter = stormy ? 'saturate(.55) brightness(.7) contrast(1.05) hue-rotate(-8deg)'
    : L.n > 0.6 ? 'brightness(.6) saturate(.85)'
      : (L.n === 0 && L.dusk === 0 && L.dawn === 0) ? 'brightness(1.08) saturate(1.06)' : 'none';
  const door = o.door;
  const pan = coverPan(size.W, size.H, { x: door.x, y: door.y - 12 }, HOME_ART_ASPECT);
  // the zoom's origin is the door itself, in the pan's pixels
  const ox = (door.x / 100) * pan.pw;
  const oy = (door.y / 100) * pan.ph;
  const zoom = phase === 'enter' && !still;
  const name = residentName || 'Your resident';
  return (
    <div
      ref={boxRef}
      className={`pdr${phase === 'knock' ? ' pdr-knock' : ''}${phase === 'enter' ? ' pdr-enter' : ''}`}
      data-door={phase}
      role="img"
      aria-label={`The outside of ${name}'s home: ${o.porch}.`}
    >
      <style>{CSS}</style>
      {pan.pw > 0 ? (
        <div className="pdr-box" style={{ width: pan.pw, height: pan.ph, transform: `translate(${pan.tx}px, ${pan.ty}px)${zoom ? ' scale(3.6)' : ''}`, transformOrigin: `${ox}px ${oy}px` }}>
          {/* a painting that can't load (not hosted yet) never leaves a dark stage
              with knock buttons: the door steps aside, straight into the home */}
          <img className="pdr-paint" src={o.url} alt="" draggable="false" style={{ filter }}
            onError={() => { if (!missed.current) { missed.current = true; if (enteredRef.current) enteredRef.current(); } }} />
          <div className="pdr-lit" style={{ left: `${o.lit.x}%`, top: `${o.lit.y}%`, opacity: L.n > 0.3 ? 0.9 : 0.35 }} />
          <div className="pdr-glow" style={{ left: `${door.x}%`, top: `${door.y}%`, width: `${door.w * 2.2}%`, height: `${door.h * 2}%` }} />
          <div className="pdr-knocker" style={{ left: `${o.knocker.x}%`, top: `${o.knocker.y}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export { KNOCK_MS };
